import { AxiosInstance } from 'axios';
import { ScimUser, UpdateUserData } from '../types/ScimUser';
import { getScimConfig } from '../utils/envUtils';
import { ScimAuthService } from './ScimAuthService';
import { getAxiosInstance } from './axiosInstance';
import { extractListLevelDollarUnderscoreProps } from '../utils/listResponseMeta';
import { debugVerboseLog } from '../utils/debugLog';

export interface UsersResponse {
    users: ScimUser[];
    totalResults: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemsPerPage: number;
    filter?: string;
    /** List-level props from the API (e.g. `$debug`) */
    [key: string]: any;
}

export class ScimUserService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
        debugVerboseLog(`🔧 ScimUserService initialized with baseURL=${scimConfig.apiUrl}`);
    }



    /**
     * Get users with pagination, filtering, and sorting support
     */
    async getUsers(page: number = 1, filter?: string, sortBy?: string, abortSignal?: AbortSignal, searchtext?: string, attributes?: string[], omitSearchDialectExpression?: boolean): Promise<UsersResponse> {
        try {
            debugVerboseLog('🌐 Fetching users from API...');

            const itemsPerPage = 25;
            const startIndex = ((page - 1) * itemsPerPage) + 1;

            // Build query parameters
            const params: any = {
                startIndex: startIndex,
                count: itemsPerPage
            };

            // Add attributes if provided (comma-separated list)
            if (attributes && attributes.length > 0) {
                params.attributes = attributes.join(',');
            }

            // Add filter if provided
            if (filter) {
                params.filter = filter;
            }

            // Add searchtext if provided (for simple search); default to expression dialect unless omitted
            if (searchtext) {
                params.searchtext = searchtext;
                params.searchdialect = omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            // Add sortBy if provided
            if (sortBy) {
                params.sortBy = sortBy;
            }

            const { debugService } = await import('./DebugService');
            if (debugService.isEnabled()) {
                params.Debug = 'true';
            }

            const queryString = new URLSearchParams(params).toString();
            const displayQueryString = decodeURIComponent(queryString.replace(/\+/g, ' '));
            debugVerboseLog(`🌐 Making API call to: ${this.api.defaults.baseURL}/entities/Person?${displayQueryString}`);

            const apiResponse = await this.api.get('/entities/Person', {
                params: params,
                signal: abortSignal
            });
            debugVerboseLog(`✅ API response received: ${apiResponse.status}`);

            // Get the users for this specific page
            const pageUsers = apiResponse.data.Resources || [];
            const totalResults = apiResponse.data.totalResults || 0;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            debugVerboseLog(`📄 Fetched page ${page} data (${pageUsers.length} users of ${totalResults} total)`);

            const listLevelMeta = extractListLevelDollarUnderscoreProps(apiResponse.data);

            return {
                ...listLevelMeta,
                users: pageUsers,
                totalResults,
                currentPage: page,
                totalPages,
                hasNextPage: page < totalPages,
                hasPreviousPage: page > 1,
                itemsPerPage: itemsPerPage,
                filter: filter
            };
        } catch (error) {
            console.error('❌ Error fetching users:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Get a single user by ID
     */
    async getUser(id: string): Promise<ScimUser> {
        try {
            debugVerboseLog(`🌐 ScimUserService: Fetching user by ID: ${id}`);

            const apiResponse = await this.api.get(`/entities/Person/${id}`);

            debugVerboseLog(`📊 ScimUserService: Received user by ID response:`, apiResponse.data);
            return apiResponse.data;
        } catch (error) {
            console.error('Error fetching user:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Update a user
     */
    async updateUser(id: string, userData: UpdateUserData): Promise<ScimUser> {
        try {
            debugVerboseLog(`🌐 ScimUserService: Updating user by ID: ${id}`);

            const scimUser = this.mapToScimUser(userData, id);
            const response = await this.api.put(`/entities/Person/${id}`, scimUser);

            debugVerboseLog(`✅ ScimUserService: Successfully updated user with ID: ${id}`);
            return response.data;
        } catch (error) {
            console.error('❌ Error updating user:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Delete a user
     */
    async deleteUser(id: string): Promise<void> {
        try {
            debugVerboseLog(`🌐 ScimUserService: Deleting user by ID: ${id}`);

            await this.api.delete(`/entities/Person/${id}`);

            debugVerboseLog(`✅ ScimUserService: Successfully deleted user with ID: ${id}`);
        } catch (error) {
            console.error('❌ Error deleting user:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Search users using SCIM API with filters
     */
    async searchUsers(params: {
        searchTerm?: string;
        entityType?: string; // Changed from EntityType to string to match ScimUser type
        searchField?: string;
        page?: number;
        pageSize?: number;
        filter?: string; // Raw SCIM filter string for advanced search
        sortBy?: string; // SCIM sortBy parameter
    }): Promise<UsersResponse> {
        try {
            const page = params.page || 1;
            const pageSize = params.pageSize || 25;

            let filterQuery: string | undefined;

            // If raw filter is provided, use it directly (for advanced search)
            if (params.filter) {
                filterQuery = params.filter;
            } else {
                // Build SCIM filter query from simple parameters
                const filters: string[] = [];

                // Entity type filter
                if (params.entityType && params.entityType !== 'all') {
                    filters.push(`EntityType eq "${params.entityType}"`);
                }

                // Search term filter
                if (params.searchTerm && params.searchTerm.trim()) {
                    const searchField = params.searchField || 'all';
                    const term = params.searchTerm.trim();

                    if (searchField === 'all') {
                        // Search across multiple fields
                        const searchFields = ['userName', 'displayName', 'name.givenName', 'name.familyName', 'emails.value'];
                        const searchFilters = searchFields.map(field => `${field} sw "${term}" or ${field} co "${term}"`);
                        filters.push(`(${searchFilters.join(' or ')})`);
                    } else {
                        // Search in specific field
                        filters.push(`${searchField} sw "${term}" or ${searchField} co "${term}"`);
                    }
                }

                // Combine filters
                filterQuery = filters.length > 0 ? filters.join(' and ') : undefined;
            }

            // Use the main getUsers method which already supports SCIM filters and sorting
            return this.getUsers(page, filterQuery, params.sortBy);
        } catch (error) {
            console.error('Error searching users:', error);
            throw error;
        }
    }

    /**
     * Get users with pagination support (legacy method - use getUsers instead)
     */
    async getUsersPaginated(page: number = 1, sortBy?: string): Promise<UsersResponse> {
        try {
            // Delegate to the main getUsers method for consistency
            return this.getUsers(page, undefined, sortBy);
        } catch (error) {
            console.error('❌ Error fetching users paginated:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    private mapToScimUser(userData: UpdateUserData, id?: string): Partial<ScimUser> {
        const scimUser: Partial<ScimUser> = {
            schemas: ['urn:ietf:params:scim:schemas:core:2.0:User'],
            UserName: userData.userName,
            DisplayName: userData.displayName,
            active: true
        };

        if (id) {
            scimUser.Id = id;
        }

        if (userData.email) {
            scimUser.emails = [{
                value: userData.email,
                primary: true,
                type: "primary",
                display: userData.email
            }];
        }

        if (userData.firstName || userData.lastName) {
            scimUser.name = {
                givenName: userData.firstName,
                familyName: userData.lastName,
                formatted: `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
            };
        }

        return scimUser;
    }

    /**
     * Patch a user with custom JSON data
     */
    async patchUser(userId: string, patchData: any, entityType?: string): Promise<any> {
        try {
            debugVerboseLog(`🔧 Patching user ${userId} with data:`, patchData);

            // Determine the endpoint based on entity type
            // All entity types should use the /Entities/{entityType}/{userId} pattern
            let endpoint;
            if (entityType && entityType !== 'Person' && entityType !== 'Users') {
                // For specific entity types, use the /Entities/{entityType}/{userId} pattern
                endpoint = `/Entities/${entityType}/${userId}`;
            } else {
                // For Person/Users, use /Entities/Person/{userId}
                endpoint = `/Entities/Person/${userId}`;
            }

            const response = await this.api.patch(endpoint, patchData);

            // API returns { Entity: {...} } - extract the entity
            const entity = response.data?.Entity || response.data;
            debugVerboseLog(`✅ User patched successfully:`, entity);
            return entity;
        } catch (error) {
            console.error('❌ Error patching user:', error);

            // For patch operations, preserve more detailed error information
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as any;
                const response = axiosError.response;

                // Create a more detailed error that preserves the original response
                const detailedError = new Error(`PATCH request failed: ${response?.status} ${response?.statusText || 'Unknown error'}`);
                (detailedError as any).response = response;
                (detailedError as any).originalError = error;
                throw detailedError;
            }

            throw ScimAuthService.handleAuthError(error);
        }
    }
}
